import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { noop } from "lodash";
import cloneDeep from "lodash/cloneDeep";

import { useTranslate } from "@similarweb/i18n-provider";
import type { GraphMetricCardProps, AreaGraphProps } from "@similarweb/pro-logic-components";
import { CardGroup, GraphMetricCard } from "@similarweb/pro-logic-components";
import type { IChangeValue } from "@similarweb/ui-components";

import { isPaidSearchPagesAvailable } from "app/pages/keyword-research/utils/permissions";
import { SUMMARY_CARD_HEIGHT } from "app/pages/keyword-research/website-analysis/pages/seo-overview/summary/consts";
import type {
    IResponseData,
    ISummaryData,
    ISummaryTrendsData,
} from "app/pages/keyword-research/website-analysis/pages/seo-overview/types";
import { allTrackers } from "app/services/track/track";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { MetricContainer } from "../../StyledComponent";
import { valueFormatter } from "../../utils";

interface IAggregateMetricsProps {
    isLoading?: boolean;
    summaryData?: IResponseData;
    trendsData?: ISummaryTrendsData;
}

const defaultData = {
    summaryData: {
        /*eslint-disable @typescript-eslint/naming-convention */
        UniqueKeywords: 0,
        UniquePages: 0,
        Ads: 0,
        /* eslint-enable @typescript-eslint/naming-convention */
    } as ISummaryData,
    trendsData: {
        trends: {},
    } as ISummaryTrendsData,
};
interface IGraphTrendsData {
    series: {
        keywords: AreaGraphProps<unknown>["data"];
        pages: AreaGraphProps<unknown>["data"];
        ads: AreaGraphProps<unknown>["data"];
    };
    change: {
        keywords: IChangeValue;
        pages: IChangeValue;
        ads: IChangeValue;
    };
}
enum MetricType {
    KEYWORDS = "keywords",
    PAGES = "pages",
    ADS = "ads",
}
const initTrendsData = {
    series: {
        keywords: [
            {
                name: MetricType.KEYWORDS,
                id: `series ${MetricType.KEYWORDS}`,
                visible: true,
                data: [],
            },
        ],
        pages: [
            {
                name: MetricType.PAGES,
                id: `series ${MetricType.PAGES}`,
                visible: true,
                data: [],
            },
        ],
        ads: [
            {
                name: MetricType.ADS,
                id: `series ${MetricType.ADS}`,
                visible: true,
                data: [],
            },
        ],
    },
} as IGraphTrendsData;
function mapTrendsData(data: ISummaryTrendsData): IGraphTrendsData {
    const returnData = cloneDeep(initTrendsData);
    const trendsData = Object.entries(data.trends).reduce(
        (acc, [dateString, { uniqueKeywords, uniquePages, ads }]) => {
            const date = dayjs.utc(dateString).valueOf();
            acc.keywords.push([date, uniqueKeywords]);
            acc.pages.push([date, uniquePages]);
            acc.ads.push([date, ads]);
            return acc;
        },
        {
            keywords: [],
            pages: [],
            ads: [],
        },
    );

    returnData.series.keywords[0].data = trendsData.keywords;
    returnData.series.pages[0].data = trendsData.pages;
    returnData.series.ads[0].data = trendsData.ads;
    return returnData;
}

interface ICardParams {
    duration: string;
    key: string;
    pageFilter: object;
}
function graphMetricCardClicked(item: MetricType, cardParams: ICardParams): () => void {
    const trackCardClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `Summary/${item}/Keywords/Pages`);
    };
    switch (item) {
        case MetricType.KEYWORDS:
            return () => {
                trackCardClick();
                void proNavigator.go("organicsearch_websites_v2_traffic_keywords", cardParams);
            };
        case MetricType.PAGES:
            return () => {
                trackCardClick();
                void proNavigator.go("organicsearch_website_landingpages_v2", cardParams);
            };
        case MetricType.ADS:
            return () => {
                trackCardClick();
                void proNavigator.go("paidsearch_website_ads", cardParams);
            };
    }
}
const CONTAINER_WIDTH = "315px";
const CONTAINER_HEIGHT = `${SUMMARY_CARD_HEIGHT}px`;
export const AggregateMetrics: FC<IAggregateMetricsProps> = ({
    summaryData = defaultData.summaryData,
    trendsData = defaultData.trendsData,
    isLoading = true,
}) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();
    const cardParams = params as ICardParams;
    const normalizedTrendsData = mapTrendsData(trendsData);
    const graphProps: GraphMetricCardProps["graphProps"] = {
        chartType: chartTypes.AREA,
        granularity: GranularityNames.MONTHLY,
        tooltipValueFormatter: valueFormatter,
    };
    const ctaIconName = "arrow-slant-right";
    return (
        <MetricContainer
            width={CONTAINER_WIDTH}
            height={CONTAINER_HEIGHT}
            padding="0"
        >
            <CardGroup
                $maxWidth={CONTAINER_WIDTH}
                $height="100%"
                withDividers={true}
                groupDirection="column"
            >
                <GraphMetricCard
                    ctaIconName={ctaIconName}
                    title={translate("website.seo.overview.aggregatemetrics.keywords.title")}
                    tooltip={translate("website.seo.overview.aggregatemetrics.keywords.tooltip")}
                    value={valueFormatter(summaryData.UniqueKeywords)}
                    graphProps={{
                        data: normalizedTrendsData.series.keywords,
                        ...graphProps,
                    }}
                    onCardClick={graphMetricCardClicked(MetricType.KEYWORDS, cardParams)}
                    isLoading={isLoading}
                />
                <GraphMetricCard
                    ctaIconName={ctaIconName}
                    title={translate("website.seo.overview.aggregatemetrics.pages.title")}
                    tooltip={translate("website.seo.overview.aggregatemetrics.pages.tooltip")}
                    value={valueFormatter(summaryData.UniquePages)}
                    graphProps={{
                        data: normalizedTrendsData.series.pages,
                        ...graphProps,
                    }}
                    onCardClick={graphMetricCardClicked(MetricType.PAGES, cardParams)}
                    isLoading={isLoading}
                />
                <GraphMetricCard
                    ctaIconName={ctaIconName}
                    title={translate("website.seo.overview.aggregatemetrics.searchads.title")}
                    tooltip={translate("website.seo.overview.aggregatemetrics.searchads.tooltip")}
                    value={valueFormatter(summaryData.Ads)}
                    graphProps={{
                        data: normalizedTrendsData.series.ads,
                        ...graphProps,
                    }}
                    onCardClick={
                        isPaidSearchPagesAvailable() ? graphMetricCardClicked(MetricType.ADS, cardParams) : noop
                    }
                    onCtaClick={
                        isPaidSearchPagesAvailable() ? graphMetricCardClicked(MetricType.ADS, cardParams) : noop
                    }
                    isLoading={isLoading}
                />
            </CardGroup>
        </MetricContainer>
    );
};
